import { createAsyncThunk } from "@reduxjs/toolkit";
import { setUser } from "@store/slices/loyaltyCoreSlice";
import {
  type IUserWithUUID,
  type UserAuthenticationPayload,
} from "@store/slices/loyaltyCoreSlice/loyaltyCoreSlice.types";
import type { AppDispatch } from "@store/types";

import { getConfig } from "@jti/fe-common";
import { getIsEvo, LoyaltyApi, ReferralApi } from "@jti/rest-api";

import { fetchUserLoyaltyProfile } from "./fetchUserLoyaltyProfile";
import { fetchUserPoints } from "./fetchUserPoints";

type HandleUserPayload = { user: IUserWithUUID; isLoyaltyActive: boolean; isReferralActive: boolean };

export const fetchReferralStats = createAsyncThunk(
  "loyaltyCore/fetchReferralStats",
  async ({ uuid, ciam_token }: UserAuthenticationPayload) => {
    const referralApi = ReferralApi.init();
    const [customer, stats] = await Promise.all([
      referralApi.getCustomer(uuid, ciam_token),
      referralApi.getReferralStats(uuid, ciam_token),
    ]);
    return { code: customer.code, referralStats: stats };
  }
);

export const fetchUserTierData = createAsyncThunk(
  "loyaltyCore/fetchUserTierData",
  async ({ uuid, ciam_token }: UserAuthenticationPayload) => {
    const loyaltyApi = LoyaltyApi.init();
    const { tier_image_url, tier_name, statusCode: __, ...rest } = await loyaltyApi.getUserTierData(uuid, ciam_token);

    return {
      name: tier_name,
      image_url: tier_image_url,
      ...rest,
    };
  }
);

export const handleUser = createAsyncThunk<void, HandleUserPayload, { dispatch: AppDispatch }>(
  "loyaltyCore/handleUser",
  async ({ user, isLoyaltyActive, isReferralActive }, { dispatch }) => {
    dispatch(
      setUser({
        name: user.first_name,
        surname: user.last_name,
        ciamToken: user.ciam_token,
        uuid: user.uuid,
      })
    );

    let currentUser = {};

    if (isReferralActive) {
      try {
        const referralStats = await dispatch(
          fetchReferralStats({ uuid: user.uuid, ciam_token: user.ciam_token })
        ).unwrap();
        currentUser = referralStats;
      } catch (error) {
        console.error("Error fetching referral stats", error);
      }
    }

    if (isLoyaltyActive) {
      try {
        const config = getConfig();

        if (getIsEvo(config.LOYALTY_URL)) {
          await dispatch(fetchUserLoyaltyProfile({}));
        } else {
          const [__, tierData] = await Promise.all([
            dispatch(fetchUserPoints({ useLocalCache: true })),
            dispatch(fetchUserTierData({ uuid: user.uuid, ciam_token: user.ciam_token })).unwrap(),
          ]);
          currentUser = { ...currentUser, tier: tierData };
        }
      } catch (error) {
        console.error("Error fetching user points or tier data", error);
      }
    }
    dispatch(setUser(currentUser));
  }
);
